var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FileUploader } from 'react-drag-drop-files';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
// Internal dependencies
import { getAuthHeader, UserContext } from '../auth';
import { axiosClient } from '../axios';
import { ENDPOINTS, LOG_LEVELS, SERVICES } from '../constants';
/**
 * Upload File Button component
 * @param props UploadFileButtonSG props
 * @returns JSX Element
 */
export const UploadFileButtonSG = function (props) {
    // Extract props
    const { styles, variant, startIconSG, attachFiles, setAttachFiles, reloadInfobaseFilesFlag, setReloadInfobaseFilesFlag, uploadLoading, setUploadLoading, attachFileTypes, } = props;
    const /** User credential */ user = useContext(UserContext);
    const { /** Translation function */ t, /** Translation module */ i18n } = useTranslation();
    /**
    * Handle upload file change
    * @param file Uploaded file
    * @return Promise of success/fail
    */
    const onUploadChange = (file) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        const startTimeInSecond = moment().valueOf() / 1000;
        try {
            // Set button status is uploading
            setUploadLoading(true);
            const /** Auth headers */ authHeader = yield getAuthHeader(user);
            const /** Request headers */ headers = Object.assign(Object.assign({}, authHeader), { "Content-Type": "multipart/form-data", 'Accept-Language': i18n.language });
            const /** Http form data */ formData = new FormData();
            // Add file to form data
            formData.append('file', file);
            // Call upload file api
            const res = yield axiosClient.post(`${process.env.SERVER_URL}${ENDPOINTS.INFOBASE}${ENDPOINTS.UPLOAD_FILE}`, formData, { headers });
            const endTimeInSecond = moment().valueOf() / 1000;
            const apiResponseTime = endTimeInSecond - startTimeInSecond;
            // Record metric to newrelic
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.RECORD_METRIC}`, {
                name: 'Infobase/uploadFile',
                value: apiResponseTime,
            });
            const /** Uploaded file */ uploadedFile = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.file;
            // Set status to uploaded
            setUploadLoading(false);
            // Reload infobase files
            setReloadInfobaseFilesFlag(reloadInfobaseFilesFlag + 1);
            if (setAttachFiles && attachFiles) {
                // Add uploaded file to attach files list
                setAttachFiles([uploadedFile, ...attachFiles]);
            }
            ;
            return true;
        }
        catch (err) {
            const endTimeInSecond = moment().valueOf() / 1000;
            const apiResponseTime = endTimeInSecond - startTimeInSecond;
            // Set status to uploaded
            setUploadLoading(false);
            // Reload infobase files
            setReloadInfobaseFilesFlag(reloadInfobaseFilesFlag + 1);
            // Log error
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `UploadFileButton, onUploadChange, ${err.message}, ${(_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.status}, ${(_d = err === null || err === void 0 ? void 0 : err.response) === null || _d === void 0 ? void 0 : _d.type}, ${apiResponseTime}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            if ((_e = err === null || err === void 0 ? void 0 : err.response) === null || _e === void 0 ? void 0 : _e.status) {
                // If the error from the server,
                // notify user with the server error message
                toast.error((_g = (_f = err.response) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.message);
                return false;
            }
            ;
            // Otherwise, notify user with simple text
            toast.error(t('Something went wrong. We are figuring it out.'));
            return false;
        }
        ;
    });
    /**
     * Handle Upload File size error
     * @param file Uploaded file
     * @return Sucess/fail
     */
    const handleUploadFileSizeError = (file) => {
        toast.error(t('The file size should be less than 20 MB.'));
        return true;
    };
    return (_jsx(Box, { component: FileUploader, handleChange: onUploadChange, name: "file", types: attachFileTypes, maxSize: 20, onSizeError: handleUploadFileSizeError, children: _jsx(Box, { children: _jsx(LoadingButton, Object.assign({ color: "greenDark", loading: uploadLoading, variant: variant, sx: Object.assign({ outline: 'none', lineHeight: '20px', border: 'none', borderRadius: '100px', textTransform: 'none', boxSizing: 'border-box', alignItems: 'center', textAlign: 'center', padding: '0.625rem 1.5rem 0.625rem 1rem', '&:focus': {
                        outline: 'none',
                    } }, styles), startIcon: startIconSG }, { children: _jsx(Typography, Object.assign({ variant: 'text14', sx: { whiteSpace: 'nowrap' } }, { children: t('Upload') })) })) }) }));
};
